import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SavedPaymentMethodsData, savedPaymentMethodsKey } from '../paymentApi';
import { PaymentRemoveCardError, requestRemoveCreditCard } from './creditCardApi';

interface UseRemoveCreditCardOptions {
  onSuccess?: () => void;
}

/**
 * Removes a saved credit card. No optimistic update — the cache is only reconciled once the
 * mutation resolves, then invalidated so the list refetches from the server.
 */
export function useRemoveCreditCard({ onSuccess }: UseRemoveCreditCardOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id: string) => requestRemoveCreditCard(id),
    onSuccess: (removedCard) => {
      queryClient.setQueryData<SavedPaymentMethodsData>(savedPaymentMethodsKey, (existing) => ({
        cards: (existing?.cards ?? []).filter((card) => card.id !== removedCard.id),
        bankAccounts: existing?.bankAccounts ?? [],
      }));
      void queryClient.invalidateQueries({ queryKey: savedPaymentMethodsKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      // PaymentRemoveCardError wraps removeCreditCard's Failure variant (NOT_FOUND, DEFAULT_CARD).
      const code = error instanceof PaymentRemoveCardError ? error.code : extractGqlErrorCode(error);
      if (error instanceof Error) {
        trackException(error, { source: 'removeCreditCard', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return { submit: (id: string) => mutation.mutate(id), isPending: mutation.isPending };
}
